/**
 * /fo/shift — My Shift page.
 * Owns Open / View / Close / Handover workflows for a receptionist.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PrimaryActionBar, OperationalCard, StatusChip, OperationalButton,
  QuickActionTile,
} from "@/components/operational";
import {
  DollarSign, PlayCircle, Printer, ArrowRightLeft, LogOut, RefreshCw, Banknote,
} from "lucide-react";
import { foApi, money } from "./foApi";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import OpenShiftDialog from "./drawers/OpenShiftDialog";
import CloseShiftDrawer from "./drawers/CloseShiftDrawer";
import HandoverShiftDrawer from "./drawers/HandoverShiftDrawer";
import AddMovementDialog from "./drawers/AddMovementDialog";

export default function ShiftPage() {
  const { user } = useAuth();
  const [hotelId, setHotelId] = useState(user?.hotel_ids?.[0] || null);

  // Fallback for super-admins (empty hotel_ids) — resolve first hotel
  useEffect(() => {
    if (hotelId) return;
    api.get("/hotels").then(r => {
      const list = r.data?.items || r.data || [];
      if (list[0]?.id || list[0]?._id) setHotelId(list[0].id || list[0]._id);
    }).catch(() => {});
  }, [hotelId]);

  const [current, setCurrent] = useState(null); // { shift, movements }
  const [summary, setSummary] = useState(null);
  const [openDlg, setOpenDlg] = useState(false);
  const [closeDlg, setCloseDlg] = useState(false);
  const [handoverDlg, setHandoverDlg] = useState(false);
  const [moveDlg, setMoveDlg] = useState(null); // 'cash_drop' | 'float_adjust' | null
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await foApi.currentShift();
      setCurrent(c);
      if (c?.shift?.id) {
        const s = await foApi.shiftSummary(c.shift.id);
        setSummary(s);
      } else {
        setSummary(null);
      }
    } catch (e) {
      toast.error("Failed to load shift");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shift = current?.shift;
  const isOpen = shift?.state === "open";
  const kpis = summary?.kpis;

  return (
    <div className="space-y-6" data-testid="fo-shift-page">
      <PrimaryActionBar
        title="My Shift"
        subtitle={isOpen ? `Open since ${(shift.opened_at || "").slice(0, 19).replace("T", " ")}` : "No shift currently open"}
        actions={[
          !isOpen && { label: "Open Shift", icon: PlayCircle, tone: "primary",
                         onClick: () => setOpenDlg(true), testid: "shift-open-btn" },
          isOpen && { label: "Close Shift", icon: LogOut, tone: "danger",
                        onClick: () => setCloseDlg(true), testid: "shift-close-btn" },
          isOpen && { label: "Handover", icon: ArrowRightLeft, tone: "secondary",
                        onClick: () => setHandoverDlg(true), testid: "shift-handover-btn" },
          isOpen && { label: "Refresh", icon: RefreshCw, tone: "secondary",
                        onClick: load, testid: "shift-refresh-btn" },
          isOpen && { label: "Print Report", icon: Printer, tone: "secondary",
                        onClick: () => window.open(foApi.shiftReportUrl(shift.id), "_blank"),
                        testid: "shift-print-btn" },
        ].filter(Boolean)}
        testid="shift-actionbar"
      />

      {loading ? (
        <div className="py-16 text-center text-slate-400">Loading shift…</div>
      ) : !shift ? (
        <OperationalCard>
          <div className="py-12 text-center">
            <Banknote className="w-12 h-12 mx-auto text-slate-300 mb-3"/>
            <div className="text-[16px] font-semibold text-slate-700">No open shift</div>
            <div className="text-[13px] text-slate-500 mt-1">Open a shift to start collecting payments.</div>
            <div className="mt-4">
              <OperationalButton
                label="Open Shift" icon={PlayCircle} tone="primary"
                onClick={() => setOpenDlg(true)} testid="shift-open-btn-large"/>
            </div>
          </div>
        </OperationalCard>
      ) : (
        <>
          {/* Handover banner — visible when this shift was opened via a
              previous colleague's handover flow. */}
          {shift?.handover_notes_in && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-1" data-testid="shift-handover-banner">
              <div className="flex items-center gap-2 text-amber-900 text-[13px] font-bold uppercase tracking-wider">
                <ArrowRightLeft className="w-4 h-4"/> Handover note from previous shift
              </div>
              <div className="text-[14px] text-amber-900 whitespace-pre-wrap leading-relaxed">
                {shift.handover_notes_in}
              </div>
              <div className="text-[11.5px] text-amber-700 pt-1 border-t border-amber-200 mt-2">
                Cash received on handover: <span className="font-semibold">{money(shift.opening_float_minor || 0)}</span>
              </div>
            </div>
          )}

          {/* KPI grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="shift-kpis">
            <QuickActionTile label="Opening Float" count={money(kpis?.opening_float_minor || 0)}
                              icon={DollarSign} status="ready" testid="shift-kpi-opening"/>
            <QuickActionTile label="Cash Received" count={money(kpis?.cash_in_minor || 0)}
                              icon={DollarSign} status="active" testid="shift-kpi-cashin"/>
            <QuickActionTile label="Refunds Out" count={money(kpis?.refunds_out_minor || 0)}
                              icon={DollarSign} status={kpis?.refunds_out_minor > 0 ? "attention" : "ready"}
                              testid="shift-kpi-refunds"/>
            <QuickActionTile label="Cash Drops" count={money(kpis?.cash_drops_minor || 0)}
                              icon={DollarSign} status="pending" testid="shift-kpi-drops"/>
            <QuickActionTile label="Expected Cash" count={money(kpis?.expected_cash_minor || 0)}
                              icon={DollarSign} status="active" testid="shift-kpi-expected"/>
            <QuickActionTile label="Total Payments" count={money(kpis?.total_payments_minor || 0)}
                              icon={DollarSign} status="ready" testid="shift-kpi-payments"/>
          </div>

          {/* Movement quick actions */}
          <div className="flex gap-2 flex-wrap">
            <OperationalButton label="Cash Drop" icon={ArrowRightLeft} tone="secondary" size="secondary"
                                onClick={() => setMoveDlg("cash_drop")} testid="shift-add-drop"/>
            <OperationalButton label="Float Adjust" icon={DollarSign} tone="secondary" size="secondary"
                                onClick={() => setMoveDlg("float_adjust")} testid="shift-add-adjust"/>
          </div>

          {/* Payments + movements two-column */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <OperationalCard testid="shift-movements-card">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">
                  Movements ({summary?.movements?.length || 0})
                </h3>
              </div>
              {(summary?.movements || []).length === 0 ? (
                <div className="text-[13px] text-slate-400 py-4">No movements yet</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {summary.movements.map((m, i) => (
                    <li key={m.id || i} className="py-2 flex items-center justify-between text-[13px]"
                          data-testid={`shift-mv-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{m.kind}</div>
                        <div className="text-[11px] text-slate-500">
                          {(m.at || "").slice(11, 19)} · {m.reason || ""}
                        </div>
                      </div>
                      <div className={`font-semibold ${(m.kind === "refund_out" || m.kind === "cash_drop") ? "text-red-700" : "text-emerald-700"}`}>
                        {(m.kind === "refund_out" || m.kind === "cash_drop") ? "−" : "+"}{money(m.amount_minor || 0)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </OperationalCard>

            <OperationalCard testid="shift-payments-card">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">
                  Payments ({summary?.payments?.length || 0})
                </h3>
              </div>
              {(summary?.payments || []).length === 0 ? (
                <div className="text-[13px] text-slate-400 py-4">No payments taken this shift</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {summary.payments.map((p, i) => (
                    <li key={p.id || i} className="py-2 flex items-center justify-between text-[13px]"
                          data-testid={`shift-py-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{p.payment_no}</div>
                        <div className="text-[11px] text-slate-500">
                          {(p.received_at || "").slice(11, 19)} · {p.method}
                        </div>
                      </div>
                      <div className="font-semibold text-emerald-700">{money(p.amount_minor || 0)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </OperationalCard>
          </div>

          {/* Totals by method */}
          {kpis?.totals_by_method && Object.keys(kpis.totals_by_method).length > 0 && (
            <OperationalCard testid="shift-totals-card">
              <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500 mb-3">
                Totals by Method
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {Object.entries(kpis.totals_by_method).map(([method, minor]) => (
                  <div key={method} className="p-3 border border-slate-200 rounded-lg" data-testid={`shift-total-${method}`}>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">{method}</div>
                    <div className="text-[16px] font-bold">{money(minor)}</div>
                  </div>
                ))}
              </div>
            </OperationalCard>
          )}
        </>
      )}

      {/* Dialogs */}
      <OpenShiftDialog open={openDlg} onOpenChange={setOpenDlg} hotelId={hotelId}
                          onSuccess={() => { setOpenDlg(false); load(); toast.success("Shift opened"); }}/>
      {shift && (
        <>
          <CloseShiftDrawer open={closeDlg} onOpenChange={setCloseDlg} shift={shift} kpis={kpis}
                              onSuccess={() => { setCloseDlg(false); load(); toast.success("Shift closed"); }}/>
          <HandoverShiftDrawer open={handoverDlg} onOpenChange={setHandoverDlg} shift={shift} hotelId={hotelId}
                                 onSuccess={() => { setHandoverDlg(false); load(); toast.success("Shift handed over"); }}/>
          <AddMovementDialog open={!!moveDlg} onOpenChange={() => setMoveDlg(null)}
                              shiftId={shift.id} kind={moveDlg || "cash_drop"}
                              onSuccess={() => { setMoveDlg(null); load(); toast.success("Movement recorded"); }}/>
        </>
      )}
    </div>
  );
}
